import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetPromotions, useDeletePromotion, useUpdatePromotion } from "../../api/hooks/usePromotion";
import { showSuccess, showError } from "../../helpers/toast";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import Pagination from "../../components/Panigation"; // Note: Filename typo in codebase 'Panigation'
import type { Promotion } from "../../api/types/promotion.types";

interface PromotionListProps {
  searchTerm: string;
}

const PromotionList: React.FC<PromotionListProps> = ({ searchTerm }) => {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promotionToDelete, setPromotionToDelete] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  React.useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(timerId);
  }, [searchTerm]);

  // Fetch promotions
  const { data: promotionsData, isLoading, refetch, error } = useGetPromotions({
    page,
    limit,
    offset: (page - 1) * limit,
    search: debouncedSearchTerm,
  });

  const { promotions, totalCount } = useMemo(() => {
    const data = promotionsData?.data;
    let list: Promotion[] = [];
    let total = 0;

    if (Array.isArray(data)) {
        list = data;
        total = data.length;
    } else if (data && typeof data === 'object') {
         const dataObj = data as any;
         if ('list' in dataObj && Array.isArray(dataObj.list)) {
            list = dataObj.list;
            total = dataObj.totalCount || dataObj.list.length;
         } else if ('data' in dataObj && Array.isArray(dataObj.data)) {
            list = dataObj.data;
            total = dataObj.totalCount || dataObj.data.length;
         }
    }
    return { promotions: list, totalCount: total };
  }, [promotionsData]);


  // Delete mutation
  const { mutate: deletePromotion, isPending: isDeleting } = useDeletePromotion({
    onSuccess: (response) => {
      showSuccess(response.message || "Promotion deleted successfully");
      refetch();
      setDeleteDialogOpen(false);
      setPromotionToDelete(null);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to delete promotion");
    },
  });

  // Update status mutation
  const { mutate: updatePromotion, isPending: isUpdating } = useUpdatePromotion({
     onSuccess: (response) => {
        showSuccess(response.message || "Promotion updated successfully");
        refetch();
     },
     onError: (error: any) => {
        showError(error?.response?.data?.message || "Failed to update promotion");
     }
  });

  const handleDelete = (id: string) => {
    setPromotionToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (promotionToDelete) {
      deletePromotion(promotionToDelete);
    }
  };

  const handleStatusToggle = (promotion: Promotion) => (event: React.ChangeEvent<HTMLInputElement>) => {
      updatePromotion({
          id: promotion._id,
          data: {
              image: promotion.image,
              redirectUrl: promotion.redirectUrl,
              status: event.target.checked
          }
      });
  };

  const totalPages = Math.ceil(totalCount / limit);
  const onPageChange = (newPage: number) => {
      if (newPage >= 1 && newPage <= totalPages) {
          setPage(newPage);
      }
  };

  return (
    <>
    <TableContainer className="table_container">
      {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
      ) : error ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <p>Error loading promotions.</p>
          </Box>
      ) : promotions.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <p>No promotions found.</p>
          </Box>
      ) : (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Image</TableCell>
            <TableCell>Redirection Link</TableCell>
            <TableCell>Visibility</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {promotions.map((promo) => (
            <TableRow hover key={promo._id}>
              <TableCell>
                <figure className="no_radius" style={{ width: '100px', height: 'auto' }}>
                  <img src={promo.image || "/static/images/placeholder.png"} alt="Promotion" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </figure>
              </TableCell>
              <TableCell>
                  <a href={promo.redirectUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'blue', textDecoration: 'underline' }}>
                      {promo.redirectUrl}
                  </a>
              </TableCell>
              <TableCell>
                  <SwitchToggle 
                    checked={!!promo.status} 
                    onChange={handleStatusToggle(promo)}
                    disabled={isUpdating}
                  />
              </TableCell>
              <TableCell>
                <div className="table_actions v2">
                  <IconButton onClick={() => navigate("/manage-promotions/view", { state: { id: promo._id } })}>
                    <img src="/static/images/eye_icon.svg" alt="View" />
                  </IconButton>
                  <IconButton onClick={() => navigate("/manage-promotions/edit", { state: { id: promo._id } })}>
                    <img src="/static/images/edit2_icon.svg" alt="Edit" />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(promo._id)}>
                    <img src="/static/images/trash2_icon.svg" alt="Delete" />
                  </IconButton>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      )}
    </TableContainer>
     {!isLoading && !error && totalCount > 0 && (
        <Pagination
          module={promotions}
          page={page}
          onPageChange={onPageChange}
          totalPages={totalPages}
          totalCount={totalCount}
          setPage={setPage}
          limit={limit}
        />
      )}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Promotion"
        message="Are you sure you want to delete this promotion?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="error"
        isLoading={isDeleting}
      />
    </>
  );
};

export default PromotionList;
